import { useCallback, useState } from "react";
import * as Sentry from "@sentry/react";
import * as api from "../../lib/claimsApi.ts";
import { ApiError, RateLimitError, genericApiError, isSentryWorthy } from "../../lib/errors";
import type { ClaimWithSource, VerifyResponse } from "../../types/claims";

export type VerifyOutcome =
  | { status: "success"; data: VerifyResponse }
  | { status: "rate_limited"; retryAfter: number }
  | { status: "error"; error: ApiError };

interface UseVerifyClaimResult {
  isLoading: boolean;
  execute: (claim: ClaimWithSource) => Promise<VerifyOutcome>;
}

export function useVerifyClaim(): UseVerifyClaimResult {
  const [isLoading, setIsLoading] = useState(false);

  const execute = useCallback(async (claim: ClaimWithSource): Promise<VerifyOutcome> => {
    setIsLoading(true);
    Sentry.addBreadcrumb({
      category: "verification",
      message: "Verifying claim",
      level: "info",
      data: { claimId: claim.id },
    });
    try {
      const data = await api.verifyClaim(claim.id, claim.source_text);
      Sentry.addBreadcrumb({
        category: "verification",
        message: "Verification complete",
        level: "info",
        data: { claimId: claim.id, verdict: data.claim_verdict.verdict },
      });
      return { status: "success", data };
    } catch (err) {
      if (err instanceof RateLimitError) {
        return { status: "rate_limited", retryAfter: err.retryAfter ?? 3600 };
      }
      const apiError = err instanceof ApiError ? err : genericApiError(err);
      if (isSentryWorthy(err)) {
        Sentry.captureException(err, {
          tags: { "error.code": apiError.code },
          extra: { endpoint: "/claims/verify", claimId: claim.id },
        });
      }
      return { status: "error", error: apiError };
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, execute };
}
